import React from 'react';

/**
 * ResponsiveImage - renders <img> with srcset from variant_urls
 *
 * Props:
 * - src: string (required) - the main image URL
 * - variantUrls: object|null - { '480w': url, '768w': url, '1200w': url, '1920w': url }
 * - width: number|null - intrinsic width for CLS prevention
 * - height: number|null - intrinsic height for CLS prevention
 * - alt: string
 * - className: string
 * - style: object
 * - sizes: string - explicit sizes attribute, e.g. "(max-width:640px) 100vw, 50vw"
 * - priority: boolean - if true, uses fetchpriority="high" + loading="eager" (for LCP/hero)
 * - aspect: string|null - aspect ratio class like "aspect-[4/3]" or "aspect-video" (wraps in container)
 * - rounded: string|null - border-radius class like "rounded-xl"
 * - onLoad: function
 */
export default function ResponsiveImage({
    src,
    variantUrls,
    width,
    height,
    alt = '',
    className = '',
    style,
    sizes = '100vw',
    priority = false,
    aspect,
    rounded,
    onLoad,
}) {
    if (!src) return null;

    // Build srcset from variant URLs
    let srcSet = null;
    let defaultSrc = src;
    if (variantUrls && typeof variantUrls === 'object') {
        const entries = [];
        // Sort by width ascending
        const widthOrder = ['480w', '768w', '1200w', '1920w'];
        for (const key of widthOrder) {
            if (variantUrls[key]) {
                entries.push(`${variantUrls[key]} ${key}`);
            }
        }
        // Add original as largest if not already covered
        if (src && entries.length > 0) {
            entries.push(`${src} 2400w`);
        }
        if (entries.length > 0) {
            srcSet = entries.join(', ');
            // Use 1200w as default src (good middle ground)
            defaultSrc = variantUrls['1200w'] || variantUrls['768w'] || src;
        }
    }

    const imgProps = {
        src: defaultSrc,
        alt,
        className: className || undefined,
        style,
        decoding: priority ? undefined : 'async',
        loading: priority ? 'eager' : 'lazy',
        onLoad,
    };

    if (priority) {
        imgProps.fetchpriority = 'high';
    }
    if (srcSet) {
        imgProps.srcSet = srcSet;
        imgProps.sizes = sizes;
    }
    if (width && height) {
        imgProps.width = width;
        imgProps.height = height;
    }

    // If aspect ratio wrapper requested
    if (aspect) {
        return (
            <div className={`${aspect} overflow-hidden ${rounded || ''}`}>
                <img {...imgProps} className={`w-full h-full object-cover ${className || ''}`} />
            </div>
        );
    }

    return <img {...imgProps} />;
}
